// Admin — moderate incoming shop reviews. Two-tab view (pending / all),
// one-click Approve / Reject buttons that mirror the actions available
// from the email notification.
import { useEffect, useState } from "react";
import { Loader2, Star, Check, X, RefreshCcw } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const TABS = [
  { key: "pending", label: "En attente" },
  { key: "approved", label: "Publiés" },
  { key: "rejected", label: "Rejetés" },
  { key: "awaiting_customer", label: "Envoyés au client" },
];

const AdminShopReviewsPage = () => {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await adminApi.shopReviewsList(status);
      setItems(r.items || []);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);  // eslint-disable-line react-hooks/exhaustive-deps

  const moderate = async (review, action) => {
    setBusyId(review.review_id);
    try {
      await adminApi.shopReviewModerate(review.review_id, action);
      setItems((prev) => prev.filter((r) => r.review_id !== review.review_id));
    } catch (e) { alert(e.message || "Échec"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="p-6" data-testid="admin-shop-reviews">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-[color:var(--gv-ink)]">Avis boutique</h1>
          <p className="text-[13px] text-[color:var(--gv-ink)]/60 mt-1">
            Modération des avis clients envoyés après réception de la carte.
          </p>
        </div>
        <button onClick={load} className="btn-ghost" data-testid="reviews-refresh">
          <RefreshCcw size={14} /> Rafraîchir
        </button>
      </div>

      <div className="flex gap-2 mb-5 border-b border-[color:var(--gv-line)]">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            className={`px-4 py-2 text-[13px] border-b-2 transition-colors ${
              status === t.key
                ? "border-[color:var(--gv-pink)] text-[color:var(--gv-pink)] font-medium"
                : "border-transparent text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-ink)]"
            }`}
            data-testid={`reviews-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[color:var(--gv-pink)]" /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px]">{error}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[color:var(--gv-ink)]/55">Aucun avis dans cette file.</div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.review_id} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4" data-testid={`review-row-${r.review_id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display text-[16px] text-[color:var(--gv-ink)]">{r.product_title}</span>
                    <span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/40">{r.product_id}</span>
                  </div>
                  <div className="text-[13px] text-[color:var(--gv-ink)]/70">
                    <strong>{r.display_name || r.customer_name}</strong> · <span className="text-[color:var(--gv-ink)]/50">{r.customer_email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 text-[color:var(--gv-pink)] shrink-0">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} size={15} className={n <= (r.rating || 0) ? "fill-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/20"} />
                  ))}
                  <span className="ml-1 text-[12px] text-[color:var(--gv-ink)]/60">{r.rating}/5</span>
                </div>
              </div>
              {r.title && <div className="font-medium text-[14px] text-[color:var(--gv-ink)] mb-1.5">{r.title}</div>}
              {r.comment && (
                <div className="text-[13.5px] text-[color:var(--gv-ink)]/85 bg-[color:var(--gv-mist)] px-3 py-2.5 rounded-[5px] whitespace-pre-line mb-3">{r.comment}</div>
              )}
              {status === "pending" && (
                <div className="flex gap-2 justify-end pt-1 border-t border-[color:var(--gv-line)]">
                  <button
                    onClick={() => moderate(r, "reject")}
                    disabled={busyId === r.review_id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] border border-red-200 text-red-700 text-[12.5px] hover:bg-red-50 disabled:opacity-50"
                    data-testid={`reject-${r.review_id}`}
                  >
                    <X size={13} /> Rejeter
                  </button>
                  <button
                    onClick={() => moderate(r, "approve")}
                    disabled={busyId === r.review_id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] bg-green-600 text-white text-[12.5px] hover:bg-green-700 disabled:opacity-50"
                    data-testid={`approve-${r.review_id}`}
                  >
                    <Check size={13} /> Publier
                  </button>
                </div>
              )}
              {(r.moderated_at || r.submitted_at) && (
                <div className="text-[11px] text-[color:var(--gv-ink)]/45 mt-2">
                  {r.moderated_at ? `Modéré : ${new Date(r.moderated_at).toLocaleString("fr-FR")}` : `Reçu : ${new Date(r.submitted_at).toLocaleString("fr-FR")}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminShopReviewsPage;
